"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "../../lib";
import { PulseRing } from "../pulse-ring";
import {
	MAGIC_BENTO_DISCOVER_GLOW_RGB,
	type MagicBentoVariant,
	resolveMagicBentoParticleCount,
	resolveMagicBentoSpotlightRadius,
} from "./magic-bento-perf";
import {
	type MagicCardFlag,
	MagicCardListCornerFlag,
} from "./magic-tile-corner-flag";
import { MagicTorch, MagicTorchSurface } from "./magic-torch";

export type { MagicBentoVariant } from "./magic-bento-perf";
export {
	MAGIC_BENTO_DEFAULT_PARTICLE_COUNT,
	MAGIC_BENTO_DEFAULT_SPOTLIGHT_RADIUS,
	MAGIC_BENTO_DISCOVER_GLOW_RGB,
	MAGIC_BENTO_MENU_PARTICLE_COUNT,
	MAGIC_BENTO_MENU_SPOTLIGHT_RADIUS,
	resolveMagicBentoParticleCount,
	resolveMagicBentoPerfDefaults,
	resolveMagicBentoSpotlightRadius,
} from "./magic-bento-perf";

/**
 * Optional hover “+ NEW …” control. Pass a pre-built `node` from saas (e.g. PrimaryCta)
 * so `@repo/ui` stays free of app CTA imports.
 */
export interface BentoCardHoverCta {
	node: React.ReactNode;
	onActivate: () => void;
	disabled?: boolean;
}

export interface BentoCardProps {
	color?: string;
	title?: string;
	description?: string;
	label?: string;
	image?: {
		/** Public path or absolute URL. */
		src: string;
		alt: string;
	};
	textAutoHide?: boolean;
	disableAnimations?: boolean;
	/** Optional in-app path; use with `onBentoCardActivate` for navigation. */
	href?: string;
	/** Non-interactive: no hover lift, tilt, or activation. */
	disabled?: boolean;
	/** When true, shows a loading veil + spinner and prevents activation. */
	loading?: boolean;
	/** Optional absolute overlay content (rendered on top of the card). */
	overlay?: React.ReactNode;
	/**
	 * Hover CTA payload. Ignored unless parent MagicBento has `showHoverCta`.
	 */
	hoverCta?: BentoCardHoverCta;
	/** Optional list-style corner flags (same geometry as org entity list tiles). */
	flags?: readonly MagicCardFlag[];
	showNotifyDot?: boolean;
}

/**
 * Hover reveal — same max-width/opacity timing as entity-tile hover CTA.
 * Sits in `card__header` (flex) so it stays top-right, inset with the label
 * padding, and vertically centered with the top-left label text. Do not use
 * absolute on a direct MagicTorchSurface child — torch forces `position:relative`
 * on those and collapses absolute placement to the top-left flow origin.
 */
export const BENTO_HOVER_CTA_REVEAL_CLASS = cn(
	"pointer-events-none flex shrink-0 items-center overflow-hidden",
	"max-w-0 opacity-0",
	"transition-[max-width,opacity] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
	"group-hover:ease-[cubic-bezier(0.4,0,0.2,1)]",
	"group-focus-visible:ease-[cubic-bezier(0.4,0,0.2,1)]",
	"group-hover:pointer-events-auto group-hover:max-w-[12rem] group-hover:overflow-visible group-hover:opacity-100",
	"group-focus-visible:pointer-events-auto group-focus-visible:max-w-[12rem] group-focus-visible:overflow-visible group-focus-visible:opacity-100",
);

export function shouldRenderBentoHoverCta(
	showHoverCta: boolean | undefined,
	hoverCta: BentoCardHoverCta | undefined,
): boolean {
	return Boolean(showHoverCta && hoverCta);
}

function BentoCardNotifyDot() {
	return (
		<span
			data-bento-notify-dot=""
			className={cn(
				"pointer-events-none absolute right-[6px] top-[6px] z-40",
				"translate-x-1/2 -translate-y-1/2",
				"inline-flex size-2 items-center justify-center rounded-full",
				"bg-red-500 ring-2 ring-[#1a1d23]",
			)}
			aria-hidden
		>
			<PulseRing
				className="-z-10"
				sizePx={20}
				borderPx={2}
				borderColor="rgba(239, 68, 68, 0.65)"
				durationS={2.4}
			/>
		</span>
	);
}

function BentoCardLoadingOverlay({
	roundedFull = false,
}: {
	roundedFull?: boolean;
}) {
	return (
		<div
			className={cn(
				"pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-white/20 backdrop-blur-sm dark:bg-black/20",
				roundedFull ? "rounded-full" : "rounded-[20px]",
			)}
			aria-hidden
		>
			<div className="h-9 w-9 animate-spin rounded-full border-2 border-gray-300 border-t-gray-800 dark:border-white/20 dark:border-t-white/80" />
		</div>
	);
}

const BENTO_LABEL_MENU_INTERACTIVE =
	"card__label text-xs font-black uppercase tracking-wide text-gray-600 transition-colors duration-300 group-hover:text-gray-900 dark:text-white/60 dark:group-hover:text-white/90 group-data-[active]:text-gray-900 dark:group-data-[active]:text-white/90";

const BENTO_LABEL_MENU_DISABLED =
	"card__label text-xs font-black uppercase tracking-wide text-gray-300 dark:text-white/30";

const BENTO_LABEL_INTERACTIVE =
	"card__label text-base font-black uppercase tracking-wide text-gray-300 transition-colors duration-300 group-hover:text-gray-800 dark:text-white/30 dark:group-hover:text-white/70 group-data-[active]:text-gray-800 dark:group-data-[active]:text-white/70";

const BENTO_LABEL_DISABLED =
	"card__label text-base font-black uppercase tracking-wide text-gray-300 dark:text-white/30";

const BENTO_IMG_INTERACTIVE =
	"h-full w-full object-contain opacity-50 grayscale transition-[filter,opacity] duration-300 ease-out group-hover:opacity-100 group-hover:grayscale-0 group-data-[active]:opacity-100 group-data-[active]:grayscale-0 group-focus-visible:opacity-100 group-focus-visible:grayscale-0";

const BENTO_IMG_DISABLED =
	"h-full w-full object-contain opacity-50 grayscale transition-none";

const BENTO_IMG_MENU_INTERACTIVE =
	"mx-auto h-auto max-h-8 w-auto max-w-8 object-contain opacity-50 grayscale transition-[filter,opacity] duration-300 ease-out group-hover:opacity-100 group-hover:grayscale-0 group-data-[active]:opacity-100 group-data-[active]:grayscale-0 group-focus-visible:opacity-100 group-focus-visible:grayscale-0";

const BENTO_IMG_MENU_DISABLED =
	"mx-auto h-auto max-h-8 w-auto max-w-8 object-contain opacity-50 grayscale transition-none";

const BENTO_IMG_MENU_COMPACT_INTERACTIVE =
	"mx-auto h-auto max-h-6 w-auto max-w-6 object-contain opacity-50 grayscale transition-[filter,opacity] duration-300 ease-out group-hover:opacity-100 group-hover:grayscale-0 group-data-[active]:opacity-100 group-data-[active]:grayscale-0 group-focus-visible:opacity-100 group-focus-visible:grayscale-0";

const BENTO_IMG_MENU_COMPACT_DISABLED =
	"mx-auto h-auto max-h-6 w-auto max-w-6 object-contain opacity-50 grayscale transition-none";

const BENTO_LABEL_MENU_COMPACT_INTERACTIVE =
	"card__label text-[10px] font-black uppercase tracking-wide text-gray-600 transition-colors duration-300 group-hover:text-gray-900 dark:text-white/60 dark:group-hover:text-white/90 group-data-[active]:text-gray-900 dark:group-data-[active]:text-white/90";

const BENTO_LABEL_MENU_COMPACT_DISABLED =
	"card__label text-[10px] font-black uppercase tracking-wide text-gray-300 dark:text-white/30";

export interface BentoProps {
	/** When true, the grid stretches to the parent height (use with a height-bounded flex parent; no fixed aspect cards). */
	fillHeight?: boolean;
	textAutoHide?: boolean;
	enableStars?: boolean;
	enableSpotlight?: boolean;
	enableBorderGlow?: boolean;
	disableAnimations?: boolean;
	spotlightRadius?: number;
	particleCount?: number;
	enableTilt?: boolean;
	/**
	 * Comma-separated RGB (e.g. `217, 152, 0`) — use a **darker** / lower-luminance value for
	 * good contrast on **light** (white) card surfaces. For gold, matches completion `COMPLETION_GRAINIENT_GOLD.color2` (`#D99800`).
	 */
	glowColor?: string;
	/**
	 * Comma-separated RGB for **dark** card surfaces — the brighter accent (e.g. gold highlight `255, 192, 31`);
	 * this was the same role as the previous single `glowColor` default.
	 */
	glowColorDark?: string;
	clickEffect?: boolean;
	enableMagnetism?: boolean;
	/** When set, replaces the built-in demo cards (same length recommended). */
	cards?: BentoCardProps[];
	/**
	 * When a card is activated, other cards drop in opacity and gain a blur veil
	 * until `focusResetKey` changes (e.g. pathname).
	 */
	dimUnselectedOnClick?: boolean;
	/**
	 * Called when a card is clicked (after focus). Often `router.push(card.href)`.
	 */
	onBentoCardActivate?: (index: number, card: BentoCardProps) => void;
	/** When this value changes, any “selected” card dimming is cleared. */
	focusResetKey?: string;
	variant?: MagicBentoVariant;
	/** Player-menu drawer density — shorter menu pills and tighter padding. */
	menuDensity?: "default" | "compact";
	/**
	 * When false/omitted (default), `card.hoverCta` is ignored.
	 * When true, renders each card’s hover CTA in the label header (top-right,
	 * vertically centered with the label) without firing `onBentoCardActivate`
	 * on CTA click.
	 */
	showHoverCta?: boolean;
}

/** Deeper purple for light / white card backgrounds (higher contrast). */
const DEFAULT_GLOW_COLOR = "100, 0, 200";
/** Brighter purple for dark card backgrounds (legacy single-`glowColor` default). */
const DEFAULT_GLOW_COLOR_DARK = "132, 0, 255";
/** Neutral glow when a card is disabled (light mode). */
const DISABLED_GLOW_COLOR = "210, 210, 210";
/** Neutral glow when a card is disabled (dark mode). */
const DISABLED_GLOW_COLOR_DARK = "120, 120, 120";
const MOBILE_BREAKPOINT = 768;

function withAlpha(color: string, alpha: number): string {
	const c = color.trim();
	if (c.startsWith("rgba(")) {
		const inner = c.slice(5, -1);
		const parts = inner.split(",").map((x) => x.trim());
		if (parts.length >= 3) {
			return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`;
		}
	}
	if (c.startsWith("rgb(")) {
		const inner = c.slice(4, -1);
		const parts = inner.split(",").map((x) => x.trim());
		if (parts.length >= 3) {
			return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`;
		}
	}
	if (c.startsWith("#")) {
		const hex = c.slice(1);
		const full =
			hex.length === 3
				? `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
				: hex;
		if (full.length === 6) {
			const r = Number.parseInt(full.slice(0, 2), 16);
			const g = Number.parseInt(full.slice(2, 4), 16);
			const b = Number.parseInt(full.slice(4, 6), 16);
			if (
				Number.isFinite(r) &&
				Number.isFinite(g) &&
				Number.isFinite(b)
			) {
				return `rgba(${r}, ${g}, ${b}, ${alpha})`;
			}
		}
	}
	return c;
}

const cardData: BentoCardProps[] = [
	{
		title: "For You",
		description: "Personalized picks that you'll love.",
		label: "Recommended",
	},
	{
		title: "Skills",
		description: "High-value skills for your role.",
		label: "Top",
	},
	{
		title: "Topics",
		description: "Bitesize topics your colleagues are exploring right now.",
		label: "Trending",
	},
	{
		title: "Learning Paths",
		description:
			"See the top modules and courses people are completing this week.",
		label: "Popular",
	},
	{
		title: "Team Picks",
		description: "Curated content from your team.",
		label: "Promoted",
	},
	{
		title: "New Content",
		description: "Fresh & noteworthy content drops.",
		label: "Recent",
	},
];

const BentoCardGrid: React.FC<{
	children: React.ReactNode;
	fillHeight?: boolean;
}> = ({ children, fillHeight = false }) => (
	<div
		className={
			fillHeight
				? "relative flex h-full min-h-0 w-full flex-1 flex-col select-none overflow-visible p-0"
				: "relative grid h-full w-full select-none gap-2 overflow-visible p-0"
		}
		style={{ fontSize: "clamp(1rem, 0.9rem + 0.5vw, 1.5rem)" }}
	>
		{children}
	</div>
);

const useMobileDetection = () => {
	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		const checkMobile = () =>
			setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);

		checkMobile();
		window.addEventListener("resize", checkMobile);

		return () => window.removeEventListener("resize", checkMobile);
	}, []);

	return isMobile;
};

/** Matches `next-themes` / Tailwind: `class="dark"` on `documentElement`. */
function useDocumentDarkClass(): boolean {
	const [isDark, setIsDark] = useState(false);

	useEffect(() => {
		if (typeof document === "undefined") {
			return;
		}
		const root = document.documentElement;
		const read = () => {
			setIsDark(root.classList.contains("dark"));
		};
		read();
		const obs = new MutationObserver(read);
		obs.observe(root, { attributes: true, attributeFilter: ["class"] });
		return () => {
			obs.disconnect();
		};
	}, []);

	return isDark;
}

const MagicBento: React.FC<BentoProps> = ({
	fillHeight = false,
	textAutoHide = true,
	enableStars = true,
	enableSpotlight = true,
	enableBorderGlow = true,
	disableAnimations = false,
	spotlightRadius: spotlightRadiusProp,
	particleCount: particleCountProp,
	enableTilt = false,
	glowColor = DEFAULT_GLOW_COLOR,
	glowColorDark = DEFAULT_GLOW_COLOR_DARK,
	clickEffect = true,
	enableMagnetism = true,
	cards: cardsFromProps,
	dimUnselectedOnClick = false,
	onBentoCardActivate,
	focusResetKey,
	variant = "default",
	menuDensity = "default",
	showHoverCta = false,
}) => {
	const gridRef = useRef<HTMLDivElement>(null);
	const isMobile = useMobileDetection();
	const isDark = useDocumentDarkClass();
	const isMenu = variant === "menu";
	const isMenuCompact = isMenu && menuDensity === "compact";
	const particleCount = resolveMagicBentoParticleCount(
		variant,
		particleCountProp,
	);
	const spotlightRadius = resolveMagicBentoSpotlightRadius(
		variant,
		spotlightRadiusProp,
	);
	const menuGlowLight =
		glowColor === DEFAULT_GLOW_COLOR
			? MAGIC_BENTO_DISCOVER_GLOW_RGB
			: glowColor;
	const menuGlowDark =
		glowColorDark === DEFAULT_GLOW_COLOR_DARK
			? MAGIC_BENTO_DISCOVER_GLOW_RGB
			: glowColorDark;
	const effectiveGlowLight = isMenu ? menuGlowLight : glowColor;
	const effectiveGlowDark = isMenu ? menuGlowDark : glowColorDark;
	const resolvedGlow = isDark ? effectiveGlowDark : effectiveGlowLight;
	const shouldDisableAnimations = disableAnimations || isMobile;
	const canStars = enableStars && !shouldDisableAnimations;
	const resolvedCardData = cardsFromProps ?? cardData;
	const [focusIndex, setFocusIndex] = useState<number | null>(null);

	useEffect(() => {
		setFocusIndex(null);
	}, [focusResetKey]);

	const pickCard = useCallback(
		(index: number) => {
			const card = resolvedCardData[index];
			if (!card || card.disabled) {
				return;
			}
			if (dimUnselectedOnClick) {
				setFocusIndex(index);
			}
			onBentoCardActivate?.(index, card);
		},
		[dimUnselectedOnClick, onBentoCardActivate, resolvedCardData],
	);

	return (
		<div
			className={
				fillHeight
					? "flex h-full min-h-0 w-full flex-1 flex-col"
					: "w-full"
			}
		>
			<style>
				{`
          .bento-section {
            --glow-color: ${resolvedGlow};
            --border-color: #392e4e;
            --background-dark: #141924;
            --white: hsl(0, 0%, 100%);
            container-type: inline-size;
            /* Used to compute fill-mode min-height. */
            --bento-gap: 8px; /* matches gap-2 */
            --bento-rows: 3;
            --bento-card-min-h: 200px;
          }

          .bento-section .magic-torch-surface.card.magic-torch-surface--border-glow {
            /* Match rounded-[20px] so ::after and clip-path use the same radius (avoids square mask/gradient corners). */
            --bento-card-radius: 20px;
            border-radius: var(--bento-card-radius);
            isolation: isolate;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
          }

          .bento-section.bento-section--menu .magic-torch-surface.card.magic-torch-surface--border-glow {
            --bento-card-radius: 9999px;
            border-radius: 9999px;
          }

          .bento-section.bento-section--menu .magic-torch-surface.card {
            overflow: visible;
          }

          .bento-section.bento-section--menu .magic-torch-surface.card > .bento-card-menu-shell {
            /* Absolute fill beats torch child position:relative so top:0 is the card rim. */
            position: absolute;
            inset: 0;
            z-index: 3;
            min-height: 0;
          }

          .bento-section.bento-section--menu .bento-card-menu-shell > [data-bento-notify-dot] {
            z-index: 40;
          }

          .bento-section.bento-section--menu .bento-card-menu-layout {
            position: absolute;
            inset: 0;
            z-index: 1;
            display: grid;
            grid-template-columns: 2.5rem minmax(0, 1fr);
            align-items: center;
            gap: 0;
            padding: 0.375rem 0.75rem 0.375rem 0.5rem;
          }

          .bento-section.bento-section--menu .bento-card-menu-layout__image {
            display: flex;
            height: 100%;
            width: 100%;
            flex-shrink: 0;
            align-items: center;
            justify-content: center;
          }

          .bento-section.bento-section--menu .bento-card-menu-layout__image img {
            display: block;
          }

          .bento-section.bento-section--menu .bento-card-menu-layout__label {
            display: flex;
            min-width: 0;
            align-items: center;
            justify-content: center;
            text-align: center;
          }

          /*
           * Flush to the card's top border-box edge (outer rim). Use flex (not a block
           * with an inline-flex child) so no anonymous line-box strut insets the ribbon.
           * Torch forces position:relative on direct surface children — keep the banner
           * nested under the shell and absolutely positioned there instead.
           */
          .bento-section.bento-section--menu .bento-card-menu-coming-soon {
            position: absolute;
            top: 0;
            left: 50%;
            z-index: 8;
            display: flex;
            justify-content: center;
            transform: translateX(-50%);
            pointer-events: none;
            margin: 0;
            padding: 0;
            line-height: 0;
          }

          .bento-section.bento-section--menu-compact .magic-torch-surface.card {
            height: 2.75rem;
            min-height: 2.75rem;
            max-height: 2.75rem;
          }

          .bento-section.bento-section--menu-compact .bento-card-menu-layout {
            grid-template-columns: 2rem minmax(0, 1fr);
            gap: 0.25rem;
            padding: 0.25rem 0.625rem 0.25rem 0.375rem;
          }

          .bento-section .card-responsive-menu {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            width: 100%;
          }

          .bento-section .magic-torch-surface.card.magic-torch-surface--border-glow:not([data-bento-card-disabled]):hover {
            box-shadow: 0 4px 20px rgba(46, 24, 78, 0.4), 0 0 30px rgba(var(--card-glow-color-rgb, ${resolvedGlow}), 0.2);
          }

          .bento-section .card.card--border-glow[data-active]:not([data-bento-card-disabled]) {
            box-shadow: 0 4px 20px rgba(46, 24, 78, 0.4), 0 0 30px rgba(var(--card-glow-color-rgb, ${resolvedGlow}), 0.2);
          }

          /* Disabled cards: keep the same glow geometry but with a neutral glow color. */
          .bento-section .magic-torch-surface.card.magic-torch-surface--border-glow[data-bento-card-disabled]:hover {
            box-shadow: 0 4px 20px rgba(46, 24, 78, 0.2), 0 0 30px rgba(var(--card-glow-color-rgb, ${resolvedGlow}), 0.2);
          }
          
          .card-responsive {
            grid-template-columns: 1fr;
            width: 100%;
            margin: 0;
            padding: 0;
          }

          /* Cards with images: switch internal layout based on container width (not viewport). */
          .bento-section .bento-card-image-layout {
            display: grid;
            min-height: 0;
            flex: 1;
            grid-template-columns: 1fr;
            grid-template-rows: auto 1fr auto;
            grid-template-areas:
              "header"
              "image"
              "content";
            gap: 0;
          }

          .bento-section .bento-card-image-layout__header {
            grid-area: header;
          }

          .bento-section .bento-card-image-layout__content {
            grid-area: content;
            min-width: 0;
          }

          .bento-section .bento-card-image-layout__image {
            grid-area: image;
            display: flex;
            min-height: 0;
            align-items: center;
            justify-content: center;
            padding: 0;
          }

          @container (max-width: 899px) {
            .bento-section .bento-card-image-layout {
              grid-template-columns: 3fr 2fr;
              grid-template-rows: auto 1fr;
              grid-template-areas:
                "header image"
                "content image";
              column-gap: 1rem;
              row-gap: 0;
              align-items: stretch;
            }

            /* Keep the second text group anchored to the bottom of the left column. */
            .bento-section .bento-card-image-layout__content {
              display: flex;
              flex-direction: column;
              justify-content: flex-end;
              min-height: 0;
            }

            .bento-section .bento-card-image-layout__image {
              padding: 0;
            }
          }
          
          @container (min-width: 680px) {
            .bento-section:not(.bento-section--menu) .card-responsive {
              grid-template-columns: repeat(2, 1fr);
            }
          }
          
          @container (min-width: 900px) {
            .bento-section:not(.bento-section--menu) .card-responsive {
              grid-template-columns: repeat(4, 1fr);
            }
            
            .bento-section:not(.bento-section--menu) .card-responsive .card:nth-child(3) {
              grid-column: span 2;
              grid-row: span 2;
            }
            
            .bento-section:not(.bento-section--menu) .card-responsive .card:nth-child(4) {
              grid-column: 1 / span 2;
              grid-row: 2 / span 2;
            }
            
            .bento-section:not(.bento-section--menu) .card-responsive .card:nth-child(6) {
              grid-column: 4;
              grid-row: 3;
            }
          }
          
          .text-clamp-1 {
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 1;
            line-clamp: 1;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          
          .text-clamp-2 {
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
            line-clamp: 2;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          
          @container (max-width: 679px) {
            .bento-section:not(.bento-section--menu) .card-responsive {
              grid-template-columns: 1fr;
              width: 100%;
              margin: 0;
              padding: 0;
            }
            
            .bento-section:not(.bento-section--menu) .card-responsive:not(.bento-card-responsive-fill) .card {
              width: 100%;
              min-height: 180px;
            }
          }

          .bento-card-responsive-fill {
            display: grid;
            height: 100%;
            min-height: 0;
            width: 100%;
            flex: 1 1 0%;
            align-content: stretch;
          }

          /*
           * Fill-mode fits the available shell band (parent must constrain height
           * via EntityPageShell fillContentViewport). Do not force a calc()
           * min-height — that oversized the grid and scrolled the page. Narrow
           * single-column still floors rows so tiles remain usable and the
           * fillHeight scrollport can absorb overflow.
           */
          @container (min-width: 680px) {
            .bento-section {
              --bento-rows: 3;
              --bento-card-min-h: 200px;
            }
            .bento-card-responsive-fill {
              grid-template-rows: repeat(3, minmax(0, 1fr));
            }
          }

          @container (min-width: 1024px) {
            .bento-section {
              --bento-rows: 3;
              --bento-card-min-h: 200px;
            }
            .bento-card-responsive-fill {
              grid-template-rows: repeat(3, minmax(0, 1fr));
            }
          }

          @container (max-width: 679px) {
            .bento-section {
              --bento-rows: 6;
              --bento-card-min-h: 180px;
            }
            /*
             * Narrow single-column fill mode: minmax(0, 1fr) splits viewport height into six tiny
             * tracks while tiles enforce --bento-card-min-h and overflow-visible, so rows bleed over
             * each other. Floor each row at the tile minimum so the scrollport grows instead.
             */
            .bento-card-responsive-fill {
              grid-template-rows: repeat(
                6,
                minmax(var(--bento-card-min-h), 1fr)
              );
            }
            .bento-card-responsive-fill .card {
              min-height: var(--bento-card-min-h);
            }
          }
        `}
			</style>

			<MagicTorch
				torchRootRef={gridRef}
				className={cn(
					"bento-section magic-torch-root relative w-full",
					isMenu && "bento-section--menu",
					isMenuCompact && "bento-section--menu-compact",
					fillHeight && "flex h-full min-h-0 flex-1 flex-col",
				)}
				enableSpotlight={enableSpotlight}
				disableAnimations={shouldDisableAnimations}
				glowColor={glowColor}
				glowColorDark={glowColorDark}
				spotlightRadius={spotlightRadius}
				surfaceBorderRadius={isMenu ? "9999px" : "20px"}
				defaultEnableParticles={canStars}
				defaultParticleCount={particleCount}
				defaultEnableTilt={enableTilt}
				defaultEnableMagnetism={enableMagnetism}
				defaultEnableBorderGlow={enableBorderGlow}
				spotlightElementClassName="magic-bento-global-spotlight"
			>
				{fillHeight ? (
					<div className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto p-6">
						<BentoCardGrid fillHeight>
							<MagicBentoCardGridContent
								cardData={resolvedCardData}
								dimUnselectedOnClick={dimUnselectedOnClick}
								focusIndex={focusIndex}
								onPickCard={pickCard}
								showHoverCta={showHoverCta}
								enableBorderGlow={enableBorderGlow}
								enableMagnetism={enableMagnetism}
								enableStars={canStars}
								enableTilt={enableTilt}
								clickEffect={clickEffect}
								fillHeight
								isDark={isDark}
								isMenu={isMenu}
								isMenuCompact={isMenuCompact}
								glowColor={resolvedGlow}
								particleCount={particleCount}
								textAutoHide={textAutoHide}
							/>
						</BentoCardGrid>
						{/*
					  Scrollport padding-bottom may not extend scroll height reliably; a trailing
					  block keeps bottom inset symmetric with `p-6` (h-6).
					*/}
						<div aria-hidden className="h-6 w-full shrink-0" />
					</div>
				) : (
					<BentoCardGrid>
						<MagicBentoCardGridContent
							cardData={resolvedCardData}
							dimUnselectedOnClick={dimUnselectedOnClick}
							focusIndex={focusIndex}
							onPickCard={pickCard}
							showHoverCta={showHoverCta}
							enableBorderGlow={enableBorderGlow}
							enableMagnetism={enableMagnetism}
							enableStars={canStars}
							enableTilt={enableTilt}
							clickEffect={clickEffect}
							fillHeight={false}
							isDark={isDark}
							isMenu={isMenu}
							isMenuCompact={isMenuCompact}
							glowColor={resolvedGlow}
							particleCount={particleCount}
							textAutoHide={textAutoHide}
						/>
					</BentoCardGrid>
				)}
			</MagicTorch>
		</div>
	);
};

interface MagicBentoCardGridContentProps {
	cardData: BentoCardProps[];
	dimUnselectedOnClick: boolean;
	focusIndex: number | null;
	onPickCard: (index: number) => void;
	showHoverCta: boolean;
	fillHeight: boolean;
	isDark: boolean;
	isMenu: boolean;
	isMenuCompact: boolean;
	textAutoHide: boolean;
	enableStars: boolean;
	enableBorderGlow: boolean;
	particleCount: number;
	glowColor: string;
	enableTilt: boolean;
	clickEffect: boolean;
	enableMagnetism: boolean;
}

function BentoCardHoverCtaHost({
	hoverCta,
}: {
	hoverCta: BentoCardHoverCta;
}) {
	return (
		<div
			data-bento-hover-cta=""
			className={BENTO_HOVER_CTA_REVEAL_CLASS}
		>
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: CTA node supplies its own button; host only stops surface activate. */}
			{/* biome-ignore lint/a11y/noStaticElementInteractions: stopPropagation wrapper for torch surface. */}
			<div
				className={cn(
					"relative z-20",
					hoverCta.disabled && "pointer-events-none opacity-50",
				)}
				onClick={(event) => {
					event.stopPropagation();
					event.preventDefault();
					if (hoverCta.disabled) {
						return;
					}
					hoverCta.onActivate();
				}}
				onPointerDown={(event) => {
					event.stopPropagation();
				}}
			>
				{hoverCta.node}
			</div>
		</div>
	);
}

function MagicBentoCardGridContent({
	cardData,
	dimUnselectedOnClick,
	focusIndex,
	onPickCard,
	showHoverCta,
	fillHeight,
	isDark,
	isMenu,
	isMenuCompact,
	textAutoHide,
	enableStars,
	enableBorderGlow,
	particleCount,
	glowColor,
	enableTilt,
	clickEffect,
	enableMagnetism,
}: MagicBentoCardGridContentProps) {
	return (
		<div
			className={cn(
				"grid gap-2",
				isMenu
					? "card-responsive-menu"
					: `card-responsive${
							fillHeight
								? " bento-card-responsive-fill h-full min-h-0 flex-1"
								: ""
						}`,
			)}
		>
			{cardData.map((card, index) => {
				const isLoading = Boolean(card.loading);
				const cardDisabled = Boolean(card.disabled) || isLoading;
				const bentoLabelClassName = cardDisabled
					? isMenu
						? isMenuCompact
							? BENTO_LABEL_MENU_COMPACT_DISABLED
							: BENTO_LABEL_MENU_DISABLED
						: BENTO_LABEL_DISABLED
					: isMenu
						? isMenuCompact
							? BENTO_LABEL_MENU_COMPACT_INTERACTIVE
							: BENTO_LABEL_MENU_INTERACTIVE
						: BENTO_LABEL_INTERACTIVE;
				const bentoImgClassName = cardDisabled
					? isMenu
						? isMenuCompact
							? BENTO_IMG_MENU_COMPACT_DISABLED
							: BENTO_IMG_MENU_DISABLED
						: BENTO_IMG_DISABLED
					: isMenu
						? isMenuCompact
							? BENTO_IMG_MENU_COMPACT_INTERACTIVE
							: BENTO_IMG_MENU_INTERACTIVE
						: BENTO_IMG_INTERACTIVE;
				const cornerFlag = card.flags?.[0];
				const isDimmed =
					dimUnselectedOnClick &&
					focusIndex != null &&
					focusIndex !== index;
				const isSurfaceActive =
					dimUnselectedOnClick &&
					focusIndex != null &&
					focusIndex === index;
				const baseClassName = cn(
					"card group relative w-full max-w-full shadow-[0_2px_8px_rgba(0,0,0,0.12)]",
					isSurfaceActive
						? "cursor-default"
						: cardDisabled
							? "cursor-not-allowed"
							: "cursor-pointer",
					isMenu
						? "block"
						: "flex flex-col justify-between",
					"bg-white p-0 font-light dark:bg-[#141924]",
					isMenu
						? isMenuCompact
							? "h-11 min-h-11 max-h-11 min-w-0 rounded-full border border-solid border-gray-200 dark:border-white/10"
							: "h-14 min-h-14 max-h-14 min-w-0 rounded-full border border-solid border-gray-200 dark:border-white/10"
						: cn(
								fillHeight
									? "h-full min-h-0 rounded-[20px]"
									: "aspect-[4/3] min-h-[200px] rounded-[20px]",
								"border border-solid border-gray-200 dark:border-white/10",
							),
				);

				const defaultSurfaceColor = isDark ? "#141924" : "#ffffff";
				const defaultBorderColor = isDark
					? "rgba(255,255,255,0.10)"
					: "rgba(229,231,235,1)"; // gray-200

				const resolvedCardGlow = cardDisabled
					? isDark
						? DISABLED_GLOW_COLOR_DARK
						: DISABLED_GLOW_COLOR
					: glowColor;

				const baseBg = card.color ?? defaultSurfaceColor;
				const cardStyle = {
					backgroundColor: cardDisabled
						? withAlpha(baseBg, 0.2)
						: baseBg,
					borderColor: defaultBorderColor,
					...(isSurfaceActive ? { cursor: "default" } : {}),
				} as React.CSSProperties;

				return (
					<MagicTorchSurface
						key={index}
						className={baseClassName}
						style={cardStyle}
						disabled={cardDisabled}
						active={isSurfaceActive}
						dimmed={isDimmed}
						glowColorRgb={resolvedCardGlow}
						particleGlowRgb={enableStars ? glowColor : undefined}
						enableParticles={enableStars}
						particleCount={particleCount}
						enableBorderGlow={enableBorderGlow}
						enableGlassBorderRing={enableBorderGlow}
						enableTilt={enableTilt}
						enableMagnetism={enableMagnetism}
						clickEffect={clickEffect}
						compatibilityBentoCardDisabled
						onActivate={() => onPickCard(index)}
						wrapChildrenInSurfaceInner={false}
					>
						{isLoading ? (
							<BentoCardLoadingOverlay roundedFull={isMenu} />
						) : null}
						{card.overlay ? card.overlay : null}
						{isMenu ? (
							<div className="bento-card-menu-shell">
								{card.showNotifyDot ? (
									<BentoCardNotifyDot />
								) : null}
								<div
									className={cn(
										"bento-card-menu-layout",
										cardDisabled && "opacity-30",
									)}
								>
									{card.image?.src ? (
										<div className="bento-card-menu-layout__image">
											<img
												src={card.image.src}
												alt={card.image.alt}
												className={bentoImgClassName}
												loading="lazy"
											/>
										</div>
									) : null}
									<div className="bento-card-menu-layout__label">
										<span className={bentoLabelClassName}>
											{card.title ?? card.label}
										</span>
									</div>
								</div>
								{cornerFlag ? (
									<div className="bento-card-menu-coming-soon">
										<MagicCardListCornerFlag
											flag={cornerFlag}
											placement="menu"
										/>
									</div>
								) : null}
							</div>
						) : (
						<>
						{card.showNotifyDot ? <BentoCardNotifyDot /> : null}
						<div
							className={cn(
								"flex min-h-0 w-full flex-1 flex-col justify-between px-5 pb-5 pt-5",
								cardDisabled && "opacity-30",
							)}
						>
							{card.image?.src ? (
								<div className="bento-card-image-layout">
									<div className="bento-card-image-layout__header">
										<div className="card__header relative flex items-center justify-between gap-3">
											<span
												className={bentoLabelClassName}
											>
												{card.label}
											</span>
											{shouldRenderBentoHoverCta(
												showHoverCta,
												card.hoverCta,
											) && card.hoverCta ? (
												<BentoCardHoverCtaHost
													hoverCta={card.hoverCta}
												/>
											) : null}
										</div>
									</div>
									<div className="bento-card-image-layout__image">
										<img
											src={card.image.src}
											alt={card.image.alt}
											className={bentoImgClassName}
											loading="lazy"
										/>
									</div>
									<div className="bento-card-image-layout__content">
										<div className="card__content relative flex flex-col">
											<h3
												className={cn(
													"card__title m-0 mb-1 text-base font-extrabold text-gray-900 dark:text-white",
													textAutoHide
														? "text-clamp-1"
														: "",
												)}
											>
												{card.title}
											</h3>
											<p
												className={cn(
													"card__description text-xs leading-5 text-gray-600 dark:text-white/90",
													textAutoHide
														? "text-clamp-2"
														: "",
												)}
											>
												{card.description}
											</p>
										</div>
									</div>
								</div>
							) : (
								<>
									<div className="card__header relative flex items-center justify-between gap-3">
										<span className={bentoLabelClassName}>
											{card.label}
										</span>
										{shouldRenderBentoHoverCta(
											showHoverCta,
											card.hoverCta,
										) && card.hoverCta ? (
											<BentoCardHoverCtaHost
												hoverCta={card.hoverCta}
											/>
										) : null}
									</div>
									<div className="card__content relative flex flex-col">
										<h3
											className={cn(
												"card__title m-0 mb-1 text-base font-extrabold text-gray-900 dark:text-white",
												textAutoHide
													? "text-clamp-1"
													: "",
											)}
										>
											{card.title}
										</h3>
										<p
											className={cn(
												"card__description text-xs leading-5 text-gray-600 dark:text-white/90",
												textAutoHide
													? "text-clamp-2"
													: "",
											)}
										>
											{card.description}
										</p>
									</div>
								</>
							)}
						</div>
						</>
						)}
						{!isMenu && cornerFlag ? (
							<MagicCardListCornerFlag
								flag={cornerFlag}
								placement="stack"
							/>
						) : null}
					</MagicTorchSurface>
				);
			})}
		</div>
	);
}

export default MagicBento;
